import { useMutation, useQuery } from '@tanstack/react-query';
import { PROJECT_TYPES, hasRoleAtLeast } from '@raro/domain';
import { Button, Field, Input, Label, Notice, Select, Title } from '@raro/ui';
import { LIVE_CADENCES, LIVE_CADENCE_LABELS, type LiveCadence } from '@raro/domain';
import { MachineFill } from '../components/MachineFill';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useBrands } from '../lib/brands';
import { PROJECT_TYPE_LABELS } from '../lib/format';
import type { Organization, Project } from '../lib/types';

export function NovoProjetoPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  // valor e horas vendidos são só de sócio
  const canSell = Boolean(me?.internalRole && hasRoleAtLeast(me.internalRole, 'partner'));
  const [params] = useSearchParams();
  const orgs = useQuery({ queryKey: ['organizations'], queryFn: () => api.get<Organization[]>('/organizations') });
  const clients = (orgs.data ?? []).filter((o) => !o.isHome);

  const [organizationId, setOrganizationId] = useState(params.get('org') ?? '');
  const [name, setName] = useState(`VER 01 / ${new Date().getFullYear()}`);
  const [type, setType] = useState<(typeof PROJECT_TYPES)[number]>('ver');
  const [startNow, setStartNow] = useState(true);
  const [cadence, setCadence] = useState<LiveCadence>('monthly');
  const [contractValue, setContractValue] = useState('');
  const [contractHours, setContractHours] = useState('');
  // marca do projeto: '' = a padrão do cliente; 'grupo' = projeto do grupo
  const [brandChoice, setBrandChoice] = useState(params.get('marca') ?? '');
  // VER localizado: vem da aba praças (?point= ou ?place=) ou é escolhido aqui. Vazio = o cliente inteiro.
  const [scope, setScope] = useState(params.get('point') ? `point:${params.get('point')}` : params.get('place') ? `place:${params.get('place')}` : '');

  const create = useMutation({
    mutationFn: async () => {
      const project = await api.post<Project>(`/organizations/${organizationId}/projects`, {
        name,
        type,
        cadence: type === 'live' ? cadence : undefined,
        contractValue: contractValue ? Number(contractValue) : undefined,
        contractHours: contractHours ? Number(contractHours) : undefined,
        status: startNow ? 'active' : 'draft',
        startedAt: startNow ? new Date().toISOString() : undefined,
        brandId: brandChoice === 'grupo' ? null : brandChoice || undefined,
        pointId: scope.startsWith('point:') ? scope.slice(6) : undefined,
        placeId: scope.startsWith('place:') ? scope.slice(6) : undefined,
      });
      if (startNow) {
        await api.post(`/projects/${project.id}/cycles`, { status: 'active', stage: 'observar', startsAt: new Date().toISOString() });
      }
      return project;
    },
    onSuccess: (project) => navigate(`/projetos/${project.id}`),
  });

  const selected = organizationId || clients[0]?.id || '';
  const places = useQuery({ queryKey: ['places', selected], enabled: Boolean(selected), queryFn: () => api.get<{ places: { place: { id: string; name: string; uf: string | null; city: string | null }; client: { points: { id: string; name: string }[] } }[] }>(`/organizations/${selected}/places`) });
  const rows = places.data?.places ?? [];
  const { brands, multi } = useBrands(selected);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setOrganizationId(selected);
    create.mutate();
  };

  return (
    <div className="rr-page rr-stack rr-stack--6">
      <div className="rr-stack rr-stack--2">
        <Label>Novo projeto</Label>
        <Title>o que vamos investigar?</Title>
      </div>

      {orgs.isSuccess && clients.length === 0 && (
        <Notice>
          ainda não há clientes. <Link to="/organizacoes/nova" style={{ textDecoration: 'underline' }}>crie o primeiro</Link>.
        </Notice>
      )}

      <form className="rr-form" onSubmit={submit}>
        <MachineFill<{ name: string; type: typeof type; cadence: LiveCadence | null; contractValue: number | null; contractHours: number | null }> form="project" organizationId={selected || null} placeholder="ex.: um VER de 90 dias para a Regina, R$ 48 mil, 160 horas, começando em outubro" onFill={(f) => { if (f.name) setName(f.name); if (f.type) setType(f.type); if (f.cadence) setCadence(f.cadence); if (f.contractValue != null) setContractValue(String(f.contractValue)); if (f.contractHours != null) setContractHours(String(f.contractHours)); }} />
        <Field label="Cliente" htmlFor="org">
          <Select id="org" value={selected} onChange={(e) => setOrganizationId(e.target.value)} required>
            {clients.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        {multi && (
          <Field label="Marca" htmlFor="brand" hint="o projeto só enxerga o material desta marca e o que é do grupo.">
            <Select id="brand" value={brandChoice || brands.find((b) => b.isDefault)?.id || ''} onChange={(e) => setBrandChoice(e.target.value)}>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              <option value="grupo">o grupo inteiro (sem marca)</option>
            </Select>
          </Field>
        )}
        {rows.length > 0 && (
          <Field label="Recorte" htmlFor="scope" hint={scope ? 'VER localizado: a Machine lê tudo como sendo deste lugar e não generaliza para o cliente inteiro.' : 'opcional. escolha uma praça ou um ponto para um VER localizado.'}>
            <Select id="scope" value={scope} onChange={(e) => setScope(e.target.value)} searchable>
              <option value="">o cliente inteiro</option>
              {rows.map((r) => (
                <optgroup key={r.place.id} label={`${r.place.name}${r.place.city ? `, ${r.place.city}` : ''} · ${r.place.uf ?? ''}`}>
                  <option value={`place:${r.place.id}`}>{r.place.name} (a praça)</option>
                  {r.client.points.map((p) => <option key={p.id} value={`point:${p.id}`}>{p.name}</option>)}
                </optgroup>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Nome" htmlFor="name" hint="ex.: VER 01 / 2027">
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
        </Field>
        <Field label="Tipo" htmlFor="type">
          <Select id="type" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            {PROJECT_TYPES.map((t) => (
              <option key={t} value={t}>
                {PROJECT_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
        </Field>
        {type === 'live' && (
          <Field label="Cadência" htmlFor="cadence" hint="o ciclo vira sozinho: a Machine sintetiza o período, encerra o ciclo e abre o próximo.">
            <Select id="cadence" value={cadence} onChange={(e) => setCadence(e.target.value as LiveCadence)}>{LIVE_CADENCES.map((c) => <option key={c} value={c}>{LIVE_CADENCE_LABELS[c]}</option>)}</Select>
          </Field>
        )}
        {canSell && <div className="rr-row" style={{ gap: 'var(--rr-space-4)', alignItems: 'flex-start' }}>
          <Field label="Valor vendido (R$)" htmlFor="cv" hint="escopo fechado. aparece no VALOR."><Input id="cv" type="number" step="100" value={contractValue} onChange={(e) => setContractValue(e.target.value)} /></Field>
          <Field label="Horas vendidas" htmlFor="ch"><Input id="ch" type="number" step="1" value={contractHours} onChange={(e) => setContractHours(e.target.value)} /></Field>
        </div>}
        <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>sem campo de pergunta: a pergunta é o produto do VER. nasce das sessões, entrevistas, pesquisa e material do cliente; a Machine propõe e um sócio bate o martelo.</p>
        <label className="rr-row" style={{ fontSize: 'var(--rr-text-sm)', color: 'var(--rr-text-2)' }}>
          <input type="checkbox" checked={startNow} onChange={(e) => setStartNow(e.target.checked)} />
          iniciar agora com Ciclo 01 em “Observar”
        </label>
        {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
        <div className="rr-form__actions">
          <Button variant="primary" type="submit" disabled={create.isPending || !selected}>
            criar projeto
          </Button>
          <Link to="/projetos">
            <Button variant="quiet">cancelar</Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
